import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { fetchMyPermissions, loginApi } from "../../lib/api";
import { isAuthenticated, postLoginPath, setAuth, setPermissions } from "../../lib/auth";
import {
  User,
  Lock,
  EyeOff,
  Eye,
  ArrowRight,
  TreePine,
  BarChart3,
  Building2,
} from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [matricula, setMatricula] = useState("");
  const [senha, setSenha] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [manterConectado, setManterConectado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);

  if (isAuthenticated()) {
    return <Navigate to={postLoginPath(location.search)} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!matricula.trim() || !senha.trim()) {
      setError("Preencha todos os campos.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await loginApi(matricula.trim(), senha);
      setAuth(res.access_token, {
        id: res.user.id,
        uuid: res.user.uuid,
        name: res.user.name,
        email: res.user.email,
        role: res.user.role,
      });
      if (manterConectado) {
        localStorage.setItem("floraiq_remember", "1");
      }
      try {
        const perms = await fetchMyPermissions();
        setPermissions(perms);
      } catch {
        // permissions load failure is non-blocking
      }
      navigate(postLoginPath(location.search), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao fazer login.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          position: relative;
          overflow: hidden;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .login-bg {
          position: absolute;
          inset: 0;
          background: url('/images/hero_cidade.png') center/cover no-repeat;
        }
        .login-bg::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            rgba(0, 63, 47, 0.82) 0%,
            rgba(0, 63, 47, 0.65) 40%,
            rgba(0, 63, 47, 0.45) 70%,
            rgba(0, 63, 47, 0.3) 100%
          );
        }

        .login-content {
          position: relative;
          z-index: 1;
          display: flex;
          width: 100%;
          min-height: 100vh;
        }

        /* Left panel */
        .login-left {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 48px 48px 48px 64px;
          color: #ffffff;
          position: relative;
        }
        .login-corner-text {
          position: absolute;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.5);
          line-height: 1.6;
        }
        .login-corner-tl { top: 32px; left: 64px; }
        .login-corner-bl { bottom: 32px; left: 64px; }
        .login-corner-tr { top: 32px; right: 32px; text-align: right; }
        .login-corner-mr { top: 50%; right: 32px; text-align: right; transform: translateY(-50%); }
        .login-corner-br { bottom: 32px; right: 32px; text-align: right; }

        .login-hero-title {
          font-size: 44px;
          font-weight: 700;
          line-height: 1.15;
          margin: 0 0 20px;
          max-width: 520px;
          color: #ffffff;
        }
        .login-hero-title em {
          font-style: normal;
          color: #6ee7a0;
        }
        .login-hero-sub {
          font-size: 17px;
          color: rgba(255, 255, 255, 0.78);
          margin: 0 0 40px;
          max-width: 420px;
          line-height: 1.6;
        }
        .login-features {
          display: flex;
          gap: 32px;
          flex-wrap: wrap;
        }
        .login-feature {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .login-feature-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .login-feature-icon svg {
          color: #6ee7a0;
        }
        .login-feature-label {
          font-size: 13px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.85);
          line-height: 1.3;
        }

        /* Right panel (card) */
        .login-right {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px;
          flex-shrink: 0;
          width: 480px;
        }
        .login-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.25);
          padding: 40px 36px 32px;
          width: 100%;
          max-width: 420px;
        }
        .login-card-brand {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 8px;
        }
        .login-card-logo {
          height: 56px;
          width: auto;
          margin-bottom: 8px;
        }
        .login-card-subtitle {
          font-size: 14px;
          color: #7d8f87;
          margin: 0 0 28px;
          text-align: center;
        }

        .login-field {
          margin-bottom: 18px;
        }
        .login-field-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #1f2d28;
          margin-bottom: 6px;
        }
        .login-field-input-wrap {
          position: relative;
        }
        .login-field-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          pointer-events: none;
        }
        .login-field-input {
          width: 100%;
          padding: 12px 12px 12px 40px;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          font-size: 14px;
          color: #1f2d28;
          background: #f9fafb;
          transition: border-color 0.2s, box-shadow 0.2s;
          outline: none;
          font-family: inherit;
        }
        .login-field-input::placeholder {
          color: #9ca3af;
        }
        .login-field-input:focus {
          border-color: #0f5c43;
          box-shadow: 0 0 0 3px rgba(15, 92, 67, 0.12);
          background: #ffffff;
        }
        .login-field-input:disabled {
          opacity: 0.6;
        }
        .login-field-eye {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #9ca3af;
          padding: 4px;
          display: flex;
          transition: color 0.15s;
        }
        .login-field-eye:hover {
          color: #4b5563;
        }

        .login-options {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
        }
        .login-checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
        }
        .login-checkbox {
          width: 16px;
          height: 16px;
          accent-color: #0f5c43;
          cursor: pointer;
        }
        .login-checkbox-text {
          font-size: 13px;
          color: #4b5563;
        }
        .login-forgot {
          font-size: 13px;
          font-weight: 600;
          color: #0f5c43;
          background: none;
          border: none;
          cursor: pointer;
          text-decoration: none;
          transition: color 0.15s;
        }
        .login-forgot:hover {
          color: #0a4533;
          text-decoration: underline;
        }

        .login-error {
          font-size: 13px;
          color: #dc2626;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 10px;
          padding: 10px 14px;
          margin-bottom: 16px;
        }

        .login-submit {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #0f5c43 0%, #168a52 100%);
          color: #ffffff;
          font-size: 15px;
          font-weight: 600;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: opacity 0.2s, transform 0.15s;
          font-family: inherit;
        }
        .login-submit:hover:not(:disabled) {
          opacity: 0.92;
          transform: translateY(-1px);
        }
        .login-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 24px 0 20px;
        }
        .login-divider-line {
          flex: 1;
          height: 1px;
          background: #e5e7eb;
        }
        .login-divider-text {
          font-size: 12px;
          color: #9ca3af;
          white-space: nowrap;
        }

        .login-institutional {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
          margin-bottom: 20px;
        }
        .login-inst-separator {
          width: 1px;
          height: 40px;
          background: #e5e7eb;
        }
        .login-inst-logo {
          height: 44px;
          width: auto;
          object-fit: contain;
        }

        .login-footer {
          text-align: center;
        }
        .login-footer-copy {
          font-size: 12px;
          font-weight: 600;
          color: #4b5563;
          margin: 0 0 2px;
        }
        .login-footer-tag {
          font-size: 11px;
          color: #9ca3af;
          margin: 0;
        }

        /* Recovery modal */
        .login-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 50;
          padding: 16px;
        }
        .login-modal {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          max-width: 380px;
          width: 100%;
          padding: 28px;
        }
        .login-modal h2 {
          font-size: 18px;
          font-weight: 700;
          color: #1f2d28;
          margin: 0 0 12px;
        }
        .login-modal p {
          font-size: 14px;
          color: #4b5563;
          margin: 0 0 8px;
          line-height: 1.6;
        }
        .login-modal-email {
          font-weight: 600;
          color: #0f5c43;
        }
        .login-modal-btn {
          width: 100%;
          padding: 10px;
          background: #0f5c43;
          color: #ffffff;
          font-size: 14px;
          font-weight: 600;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          margin-top: 16px;
          font-family: inherit;
          transition: opacity 0.15s;
        }
        .login-modal-btn:hover { opacity: 0.9; }

        /* Responsive */
        @media (max-width: 1024px) {
          .login-left { display: none; }
          .login-corner-text { display: none; }
          .login-right {
            width: 100%;
            padding: 24px 16px;
          }
          .login-page {
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .login-card {
            padding: 28px 20px 24px;
            border-radius: 16px;
          }
          .login-hero-title { font-size: 32px; }
        }
      `}</style>

      <div className="login-page">
        <div className="login-bg" />

        <div className="login-content">
          {/* Decorative corner texts */}
          <div className="login-corner-text login-corner-tl">
            Meio Ambiente<br />
            Cidades Mais Vivas<br />
            Pessoas Mais Felizes
          </div>
          <div className="login-corner-text login-corner-bl">
            Goiânia Hoje.<br />
            Um Amanhã Mais Verde.
          </div>
          <div className="login-corner-text login-corner-tr">
            Goiânia<br />
            Sustentável<br />
            Sempre
          </div>
          <div className="login-corner-text login-corner-mr">
            Natureza<br />
            Planejamento<br />
            Pessoas<br />
            Resultados
          </div>
          <div className="login-corner-text login-corner-br">
            Plantar<br />
            Preservar<br />
            Transformar
          </div>

          {/* Left: hero text */}
          <div className="login-left">
            <h1 className="login-hero-title">
              Tecnologia<br />
              a serviço de uma<br />
              <em>Goiânia mais verde.</em>
            </h1>
            <p className="login-hero-sub">
              Inteligência, dados e gestão para<br />
              um futuro urbano mais sustentável.
            </p>
            <div className="login-features">
              <div className="login-feature">
                <div className="login-feature-icon">
                  <TreePine size={20} />
                </div>
                <span className="login-feature-label">
                  Árvores<br />e áreas verdes
                </span>
              </div>
              <div className="login-feature">
                <div className="login-feature-icon">
                  <BarChart3 size={20} />
                </div>
                <span className="login-feature-label">
                  Dados para<br />melhores decisões
                </span>
              </div>
              <div className="login-feature">
                <div className="login-feature-icon">
                  <Building2 size={20} />
                </div>
                <span className="login-feature-label">
                  Cidades mais<br />sustentáveis
                </span>
              </div>
            </div>
          </div>

          {/* Right: login card */}
          <div className="login-right">
            <div className="login-card">
              <div className="login-card-brand">
                <img
                  src="/logos/floraiq_logo_oficial.webp"
                  alt="FloraIQ"
                  className="login-card-logo"
                />
              </div>
              <p className="login-card-subtitle">
                Plataforma de Inteligência Ambiental Urbana
              </p>

              <form onSubmit={handleSubmit}>
                <div className="login-field">
                  <label htmlFor="matricula" className="login-field-label">
                    Matrícula
                  </label>
                  <div className="login-field-input-wrap">
                    <span className="login-field-icon">
                      <User size={18} />
                    </span>
                    <input
                      id="matricula"
                      type="text"
                      autoComplete="username"
                      value={matricula}
                      onChange={(e) => setMatricula(e.target.value)}
                      className="login-field-input"
                      placeholder="Digite sua matrícula"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="login-field">
                  <label htmlFor="senha" className="login-field-label">
                    Senha
                  </label>
                  <div className="login-field-input-wrap">
                    <span className="login-field-icon">
                      <Lock size={18} />
                    </span>
                    <input
                      id="senha"
                      type={showSenha ? "text" : "password"}
                      autoComplete="current-password"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      className="login-field-input"
                      style={{ paddingRight: 44 }}
                      placeholder="Digite sua senha"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSenha(!showSenha)}
                      className="login-field-eye"
                      tabIndex={-1}
                      aria-label={showSenha ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="login-options">
                  <label className="login-checkbox-label">
                    <input
                      type="checkbox"
                      checked={manterConectado}
                      onChange={(e) => setManterConectado(e.target.checked)}
                      className="login-checkbox"
                      disabled={loading}
                    />
                    <span className="login-checkbox-text">Manter conectado</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowRecovery(true)}
                    className="login-forgot"
                  >
                    Esqueceu a senha?
                  </button>
                </div>

                {error && <div className="login-error">{error}</div>}

                <button
                  type="submit"
                  disabled={loading}
                  className="login-submit"
                >
                  {loading ? "Entrando..." : "Entrar"}
                  {!loading && <ArrowRight size={18} />}
                </button>
              </form>

              <div className="login-divider">
                <div className="login-divider-line" />
                <span className="login-divider-text">
                  Juntos por uma Goiânia mais verde
                </span>
                <div className="login-divider-line" />
              </div>

              <div className="login-institutional">
                <img
                  src="/logos/amma.webp"
                  alt="AMMA"
                  className="login-inst-logo"
                />
                <div className="login-inst-separator" />
                <img
                  src="/logos/prefeituradegoiania.webp"
                  alt="Prefeitura de Goiânia"
                  className="login-inst-logo"
                />
              </div>

              <div className="login-footer">
                <p className="login-footer-copy">
                  FloraIQ &copy; {new Date().getFullYear()}
                </p>
                <p className="login-footer-tag">
                  Tecnologia a serviço das pessoas e do meio ambiente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showRecovery && (
        <div className="login-modal-overlay">
          <div className="login-modal">
            <h2>Recuperar senha</h2>
            <p>
              Entre em contato com o administrador do sistema para redefinir sua
              senha de acesso.
            </p>
            <p>
              E-mail de suporte:{" "}
              <span className="login-modal-email">suporte@floraiq.com.br</span>
            </p>
            <button
              type="button"
              onClick={() => setShowRecovery(false)}
              className="login-modal-btn"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
}
